<template>
  <div>
    <div class="tier-group__options-btn-wrapper py-2">
      <KButton text-only @click="handleBackButtonClick">
        <template #prepend>
          <KIcon name="keyboard-arrow-left" class="h-3 w-2 mr-4" />
        </template>
        {{ answer.labelBackButton }}
      </KButton>
    </div>
    <div class="md:flex">
      <SelectionCard class="tier-group__selection-card" is-selected :value="answer" independent @select="handleAnswerClick">
        <SelectionCardLayout :title="answer.heading" :body="answer.description" :image="answer.image" />
      </SelectionCard>
      <transition name="reveal">
        <div v-if="isMoreInfoVisible" class="pb-4 md:pb-0 md:pr-8">
          <MoreInfo @close="handleMoreInfoClose">
            <div class="text-left" v-dompurify-html="answer.moreInfo" />
          </MoreInfo>
        </div>
      </transition>
      <div
        class="grid md:flex gap-4 md:gap-8 grid-cols-1"
        role="list"
        aria-label="available tiers"
      >
        <TierOption v-for="tier in answer.tiers" :key="tier.uid" :content="tier" :isSelected="isTierSelected(tier)" @select="handleTierClick"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import TierOption from '@/components/atoms/TierOption/index.vue'
import SelectionCard from '@/components/atoms/SelectionCard/index.vue'
import MoreInfo from '@/components/atoms/MoreInfo/index.vue'
import SelectionCardLayout from '@/components/molecules/SelectionCardLayout/index.vue'
import KButton from '@/components/atoms/Button/index.vue'
import KIcon from '@/components/atoms/Icon/index.vue'

import { Answer, Tier } from '@/model'

export default defineComponent({
  name: 'TierGroup',
  inheritAttrs: false,
  components: { TierOption, SelectionCard, SelectionCardLayout, MoreInfo, KButton, KIcon },
  emits: ['click:selection-card', 'click:tier-option', 'click:close-tier-group'],
  props: {
    answer: {
      type: Object as () => Answer,
      required: true,
      validator: (value: Answer) => value instanceof Answer
    },
    selectedTier: {
      type: Object as () => Tier,
      required: false,
      validator: (value: Tier) => value instanceof Tier
    }
  },
  setup (props, { emit }) {
    const isMoreInfoVisible = ref(props.answer.hasMoreInfo)

    const handleAnswerClick = () => {
      emit('click:selection-card', props.answer, false)
    }

    const handleBackButtonClick = () => {
      emit('click:close-tier-group')
    }

    const handleTierClick = (tier: Tier) => {
      emit('click:tier-option', tier, props.answer)
    }

    const handleMoreInfoClose = () => {
      isMoreInfoVisible.value = false
    }

    const isTierSelected = (tier: Tier) => {
      return props.selectedTier && props.selectedTier.uid === tier.uid
    }

    return { isMoreInfoVisible, handleAnswerClick, handleTierClick, handleMoreInfoClose, isTierSelected, handleBackButtonClick }
  }
})
</script>

<style>
.tier-group {
  &__selection-card {
    width: 155px;
    @apply mb-4 ml-auto mr-auto;
  }

  &__options-btn-wrapper {
    width: 190px;
    overflow: visible;
    @apply mx-auto
  }
}

@screen md {
  .tier-group {
    &__selection-card {
      width: unset;
      @apply mb-0 mr-8 ml-0;
    }

    &__options-btn-wrapper {
      width: 100%;
      @apply mx-0 text-right;
    }
  }
}

/* reveal transition */
.reveal-enter-active,
.reveal-leave-active {
  transition: clip-path 0.25s, margin-top 0.25s, opacity 0.2s;
  clip-path: inset(0);
  margin-top: theme("spacing.1");
  opacity: 1;
}

.reveal-enter-from,
.reveal-leave-to {
  clip-path: inset(100% 0 0 0);
  margin-top: -200px;
  opacity: 0;
}

@screen md {
  .reveal-enter-active,
  .reveal-leave-active {
    transition: clip-path 0.5s, margin 0.5s;
    clip-path: inset(0);
    margin-left: 0px;
    margin-top: unset;
  }

  .reveal-enter-from,
  .reveal-leave-to {
    clip-path: inset(0 0 0 100%);
    margin-left: -372px;
    margin-top: unset;
    opacity: 1;
  }
}
</style>
